import React, { ReactElement, useEffect, useState } from "react";

import "../styles.scss";
import "../scripts";

import {
  headerBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  renderSecondaryBlock,
  BlockType,
  MenuItem,
  getBackgroundColorByMode,
} from "@kameleon-core/lib";
import { Box, Flex } from "theme-ui";
import { WebflowComponentProps, webflowHOC } from "@kameleon-common/webflow-js";

import { v4 as uuidv4 } from "uuid";
import classNames from "classnames";
import { KameleonIcon, IconType } from "@kameleon-common/icons";
import { Link } from "@kameleon-common/gatsby-link";
import {
  backgroundColorModeMapper,
  themeColor,
  Variants,
} from "@kameleon-util/global-theme";
import { DropdownSmall } from "./components/dropdown-small";
import { NavigationType, layoutOptions, LogoProp } from "./types/prop-types";

import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import getEvents from "./js/webflow";

/** Navigation Header */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.ExtraSmall,
  tertiaryBlockSize: TertiaryBlockSize.None,
  blockType: BlockType.Header,
  blockTheme: BlockTheme.light,
};

const Logo = ({ url, alt, invert }: LogoProp): ReactElement => (
  <Link to="/">
    <Variants.Logo
      sx={{ filter: invert ? "invert(100%)" : themeColor("logoFilter") }}
      className="navbar1_logo-link"
      src={url}
      alt={alt}
    />
  </Link>
);

type PassThroughProps = {
  dropdownIds: string[];
  navbarId: string;
};
type NavigationWebflowType = WebflowComponentProps<
  NavigationType,
  PassThroughProps
>;

const createDropdownIds = (
  menu?: MenuItem[],
  dropdownIds?: string[]
): string[] => {
  const currentDropdownIds = [...(dropdownIds ?? [])];
  menu?.forEach((_, idx) => {
    currentDropdownIds[idx] = currentDropdownIds[idx] ?? uuidv4();
  });
  return currentDropdownIds;
};

const NavigationComponent = ({
  logo,
  SecondaryBlock,
  menu,
  layoutProps,
  passThrough,
}: NavigationWebflowType): ReactElement => {
  const [navbarId] = useState<string>(uuidv4());
  const [dropdownIds, setDropdownIds] = useState<string[]>(
    createDropdownIds(menu, [])
  );

  const menuItemHasDropdown = (menuItem: MenuItem) =>
    menuItem.subMenu ? menuItem.subMenu.length > 0 : false;

  useEffect(() => {
    const updatedDropdownIds = createDropdownIds(menu, dropdownIds);
    setDropdownIds(updatedDropdownIds);
    passThrough?.({ navbarId, dropdownIds: updatedDropdownIds });
  }, [menu]);

  const backgroundColorWithMode = backgroundColorModeMapper(
    layoutProps.backgroundColor as string
  );

  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = (): void => {
    const offset = window.scrollY;
    setIsScrolled(offset > 100);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return (): void => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const backgroundColor = getBackgroundColorByMode(
    backgroundColorWithMode,
    blockConfig.blockTheme
  );

  return (
    <Box
      className={classNames("header-navigation-default-logo-v1", {
        absolute: layoutProps?._positionAbsolute,
      })}
    >
      {layoutProps.menuAlignment === "center" && (
        <Box className="navbar1_logo--absolute">
          <Logo invert={layoutProps?._positionAbsolute} {...logo} />
        </Box>
      )}

      <Variants.Navbar
        data-animation="default"
        className={`navbar1_component w-nav ${isScrolled ? "fixed" : ""}`}
        data-easing2="ease"
        data-easing="ease"
        data-collapse="medium"
        data-w-id={navbarId}
        role="banner"
        data-duration="400"
        sx={{
          backgroundColor,
          borderRadius: isScrolled ? "1" : "0",
        }}
      >
        <Box
          className={`navbar1_container navbar1_container--${layoutProps.menuAlignment}`}
        >
          {layoutProps.menuAlignment !== "center" && (
            <Logo invert={false} {...logo} />
          )}
          <Flex
            as="nav"
            role="navigation"
            className="navbar1_menu is-page-height-tablet w-nav-menu"
            sx={{ backgroundColor }}
          >
            <div className="navbar1_menu-wrapper">
              {menu?.map((menuItem, i) => {
                let dropdownId = null;
                const hasDropdown = menuItemHasDropdown(menuItem);
                if (hasDropdown) {
                  dropdownId = dropdownIds[i];
                }

                const key = `navbar-item-${i}`;
                return (
                  <Box
                    key={key}
                    data-hover="true"
                    data-delay="200"
                    data-w-id={dropdownId}
                    className={classNames("navbar1_menu-dropdown w-dropdown")}
                  >
                    <Box
                      className={classNames("navbar1_dropdown-toggle", {
                        "w-dropdown-toggle": hasDropdown,
                      })}
                    >
                      <Link
                        activeClassName="navbar1_link--active"
                        to={menuItem.link}
                      >
                        {menuItem.label}
                      </Link>
                      {hasDropdown && (
                        <Box className="dropdown-chevron w-embed">
                          <KameleonIcon name={IconType.ArrowDown} />
                        </Box>
                      )}
                    </Box>

                    {hasDropdown && (
                      <DropdownSmall menuItems={menuItem.subMenu ?? []} />
                    )}
                  </Box>
                );
              })}
            </div>

            {renderSecondaryBlock(SecondaryBlock)}
          </Flex>

          <Variants.AnimatedMenuButton className="navbar1_menu-button w-nav-button" />
        </Box>
      </Variants.Navbar>
    </Box>
  );
};

const NavigationWithWebflow = webflowHOC<
  NavigationWebflowType,
  PassThroughProps
>(NavigationComponent, [
  {
    module: "ix2",
    getEvents: (pageId: any, passThrough): any =>
      getEvents(pageId, passThrough?.navbarId, passThrough?.dropdownIds ?? []),
  },
]);

export const Navigation = headerBlock<NavigationType>(
  blockConfig,
  NavigationWithWebflow
);
Navigation.defaultProps = getDefaultProps<NavigationType>(defaults);

export const Element: KameleonElement = {
  component: Navigation,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
